// src/components/HotdropdownV2.js
import React, { useState, useRef, useEffect } from "react";
import arrowIcon from "../assets/down-arrow.png";
import { useTranslation } from "react-i18next";
import Loader, { LOADER_SIZES } from "./Loader";
import { req_Status } from "../utils/enums";
import { useLazySearch } from "../utils/useLazySearch";

const HotdropdownV2 = ({
  initOptions,
  selected,
  setSelected,
  thick,
  flex,
  errorText,
  onChange,
  page = "invoices",
  getData = null,
  optionsName,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const scrollDown = useRef(null);
  const isFirstClick = useRef(true);
  const itemRefs = useRef([]);
  const { t } = useTranslation("general");
  const [loading, setLoading] = useState({ status: req_Status.NULL });
  // state for fetching options on scroll
  const [currentPage, setCurrentPage] = useState(1);
  // check if more data available for fetching
  const [totalPages, setTotalPages] = useState(initOptions?.totalPages || 1);
  // state to store new options
  const [options, setOptions] = useState(initOptions);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const { fetchOptions } = useLazySearch(
    loading,
    optionsName,
    setOptions,
    getData,
    setLoading,
    setTotalPages,
    null,
    setIsOpen
  );

  // state for searching functionality
  const [query, setQuery] = useState("");

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    if (query.trim()) {
      setCurrentPage(1);
      fetchOptions(query);
    } else {
      setOptions(initOptions); // Reset options if search is cleared
    }
  }, [query]);

  useEffect(() => {
    itemRefs.current = options.map(
      (_, i) => itemRefs.current[i] || React.createRef()
    );
  }, [options]);

  useEffect(() => {
    if (
      highlightedIndex >= 0 &&
      itemRefs.current[highlightedIndex] &&
      itemRefs.current[highlightedIndex].current
    ) {
      itemRefs.current[highlightedIndex].current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  const toggleDropdown = async () => {
    setIsOpen(!isOpen);
    setHighlightedIndex(0);

    if (getData && isFirstClick.current) {
      setLoading((prev) => ({ ...prev, status: req_Status.PENDING }));
      const req = await getData(currentPage, 5, true, query);
      setOptions((prevOptions) => {
        return [...prevOptions, ...(req?.data?.[optionsName] || [])];
      });

      // ** to be solved later in backend
      setTotalPages(() => req?.data?.totalPages || 1);
      isFirstClick.current = false;

      if (req?.status === 200) {
        setLoading((prev) => ({ ...prev, status: req_Status.SUCCESS }));
      } else {
        setLoading((prev) => ({ ...prev, status: req_Status.FAILED }));
      }
    }
  };

  const handleItemClick = (item) => {
    setSelected(item, page);
    setQuery("");
    if (onChange) {
      onChange(item);
    }
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === "Enter" || e.key === " ")) {
      toggleDropdown();
      return;
    }

    if (isOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex < options.length - 1 ? prevIndex + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : options.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleItemClick(options[highlightedIndex]);
      }
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
    }
  };

  // function to handle when the user
  // scrolls to the bottom of the dropdown to call more options
  const handleScrollBottom = async () => {
    if (
      scrollDown.current.scrollHeight - scrollDown.current.scrollTop ===
      scrollDown.current.clientHeight
    ) {
      if (loading.status === req_Status.PENDING || currentPage >= totalPages)
        return;
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchOptions(query, nextPage);
    }
  };

  const showError = !selected && errorText;

  // Handle input changes
  const handleInputChange = (e) => {
    const value = String(e.target.value);
    setQuery(value || "");

    if (!value.trim()) {
      setOptions(initOptions);
      setTotalPages(initOptions.totalPages);
    }
  };

  useEffect(() => {
    if (initOptions && initOptions.length > 0) {
      setOptions(initOptions);
    }
  }, [initOptions]);

  return (
    <div
      className={`c-dropdown ${flex ? "full-width-dropdown" : ""} ${disabled ? "disabled" : ""}`}
      ref={dropdownRef}
    >
      {showError && <div className="error-message">{t(errorText)}</div>}
      <div
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        className={`toggle ${isOpen ? "opened" : ""} ${thick ? "thick" : ""}`}
      >
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder={
            typeof selected === "object" && selected !== null
              ? selected?.title || selected?.name
              : selected || t("typeOrSelect")
          }
          className="input"
          onClick={(e) => e.stopPropagation()} // Prevent toggle when clicking the input
        />
        <img
          src={arrowIcon}
          alt="Dropdown arrow"
          className={`arrow-icon ${isOpen ? "rotate180" : ""}`}
        />
      </div>
      {isOpen && (
        <>
          <ul
            className="dropdown-menu"
            ref={scrollDown}
            onScroll={handleScrollBottom}
          >
            {Array.isArray(options) &&
              options.map((option, index) => (
                <li
                  key={index}
                  ref={itemRefs?.current[index]}
                  className={`dropdown-item ${
                    highlightedIndex === index ? "highlighted" : ""
                  }`}
                  onClick={() => handleItemClick(option)}
                  tabIndex={0} // Make each dropdown item focusable
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleItemClick(option);
                    }
                  }}
                >
                  {typeof option === "object"
                    ? t(option?.title) || option?.name
                    : option}
                </li>
              ))}
            {/* Display loading text at the bottom when new data is being fetched */}
            {loading.status === req_Status.PENDING && (
              <Loader size={LOADER_SIZES.SMALL} />
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default HotdropdownV2;
